import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Snackbar } from '@amzn/storm-ui';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { getUserProfile } from '../../services/user/userService';
import { UserProfileApiResponse } from '../../interfaces/user/UserInterfaces';
import useApiClient from '../../services/api';
import { useAppContext } from '../../store/appContext';
import { SET_USER_PERMISSIONS } from '../../store/actions/types';
import { UserRoles } from '../../constants/userRoles';
import { ApiError } from '../../interfaces/ApiError';
import { SnackbarContainer } from './UserDetails.styles';
import { UserProfileEndpointQueryKey } from '../TopNavigationHeader/services/endpoints';
import { unauthorizedRedirect } from '../../config/authConfig';
import { USER_STATUS } from '../../../UserManagement/constants/userStatus';
import { getRoles } from '../../../RoleManagement/services/service';
import { GetRolesApiResponse } from '../../../RoleManagement/interfaces/RoleManagementInterfaces';
import { EndpointQueryKeys as RoleEndpointQueryKeys } from '../../../RoleManagement/constants/endpoints';
import { EndpointQueryKeys as BookingEndpointQueryKeys } from '../../../BookingManagement/constants/endpoints';
import { resolveUserPermissions } from '../../utils/helpers';
import { useSyncCurrentUser } from '../../hooks/useSyncCurrentUser';

export function UserDetails() {
  const { t } = useTranslation();
  const auth = useAuth();
  const { dispatch, state } = useAppContext();
  const queryClient = useQueryClient();
  const api = useApiClient();
  const email = auth.user?.profile.email;
  const userId = email?.split('@')[0];
  const prevPermissionsRef = useRef<string[] | null>(null);
  const [showError, setShowError] = useState(true);

  const {
    data: userProfileData,
    isError,
    error,
  } = useQuery<UserProfileApiResponse, ApiError>({
    queryKey: [UserProfileEndpointQueryKey.USER_PROFILE],
    queryFn: () => getUserProfile(api),
    enabled: !!userId && auth.isAuthenticated,
    retry: false,
  });

  const {
    data: rolesData,
  } = useQuery<GetRolesApiResponse, ApiError>({
    queryKey: [RoleEndpointQueryKeys.GET_ROLES, state.selectedArtEventId],
    queryFn: () => getRoles(api, state.selectedArtEventId),
    enabled: !!userProfileData?.user && auth.isAuthenticated && !!state.selectedArtEventId,
  });

  // Sync user profile to store; fallback role is Account Executive (minimum permissions).
  const authenticatedUser =
    auth.isAuthenticated && auth.user?.profile
      ? userProfileData?.user
      : undefined;
  useSyncCurrentUser(authenticatedUser, UserRoles.ACCOUNT_EXECUTIVE);

  // Reset permissions when art event changes so edit actions are disabled while new permissions load
  useEffect(() => {
    dispatch({
      type: SET_USER_PERMISSIONS,
      payload: null,
    });
  }, [state.selectedArtEventId, dispatch]);

  // Resolve permissions from roles API once both user profile and roles data are available
  useEffect(() => {
    if (userProfileData?.user && rolesData?.roles) {
      const myUserRole = userProfileData.user.userRole || UserRoles.ACCOUNT_EXECUTIVE;
      const permissions = resolveUserPermissions(myUserRole, rolesData.roles);

      dispatch({
        type: SET_USER_PERMISSIONS,
        payload: permissions,
      });

      if (JSON.stringify(permissions) !== JSON.stringify(prevPermissionsRef.current)) {
        queryClient.invalidateQueries({
          queryKey: [BookingEndpointQueryKeys.GET_BOOKING_SHEET],
        });
      }
      prevPermissionsRef.current = permissions;
    }
  }, [userProfileData, rolesData, dispatch, queryClient]);

  // Check for not active users
  useEffect(() => {
    // Check if query failed (user might be deleted)
    if (isError && error?.statusCode === 400) {
      unauthorizedRedirect();
    }
  }, [isError, error, unauthorizedRedirect]);

  if (isError && showError) {
    if (error.statusCode === 403) {
      // The following function redirects the user to the unauthorized page since user isn't valid
      // The user is directed to /unauthorized-access page where the user is told that he/she isn't authorized to access this application
      unauthorizedRedirect();
      return null;
    } else {
      return (
        <SnackbarContainer>
          <Snackbar variant="error" onClose={() => setShowError(false)}>
            {t('user-role-fetch-api-error')}
          </Snackbar>
        </SnackbarContainer>
      );
    }
  }

  return null;
}
